import React, { useState } from "react";
import Header from "../Header";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { currencyFormat, resolveFirstListingImage, formatMileage, useCurrentUser, resolveListingTitle } from "../../utils";
import { useUserListings } from "../../services";
import Loading from "../Loading";
import { useMutation } from '@apollo/client';
import updateCarForSaleActiveMutation from '../../graphql/mutations/updateCarForSaleActive.mutation';
import relistListingMutation from '../../graphql/mutations/relistListing.mutation';
import markListingAsSoldMutation from '../../graphql/mutations/markListingAsSold.mutation';
import { useDarkMode } from "../../App";

const ListingView = () => {
  const user = useCurrentUser();
  const userId = user?.id;
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'active' | 'inactive'>('active');
  const [busyListingId, setBusyListingId] = useState<number | null>(null);

  const { data: activeData, error: activeError, loading: activeLoading, refetch: refetchActive } = useUserListings({
    variables: { sellerId: userId as number, active: true },
    skip: !userId,
  });
  const { data: inactiveData, error: inactiveError, loading: inactiveLoading, refetch: refetchInactive } = useUserListings({
    variables: { sellerId: userId as number, active: false },
    skip: !userId,
  });

  const [updateCarForSaleActive] = useMutation(updateCarForSaleActiveMutation);
  const [relistListing] = useMutation(relistListingMutation);
  const [markListingAsSold] = useMutation(markListingAsSoldMutation);

  if (!userId) {
    return <Navigate to="/login" />;
  }
  
  const getFirstImageUrl = (imageLoc: any) => resolveFirstListingImage(imageLoc, { darkMode });

  if (activeLoading || inactiveLoading) {
    return <Loading />;
  }

  if (activeError || inactiveError) {
    alert("ERROR OCCURRED WHILE TRYING TO LOAD DATA!");
  }

  const refreshLists = async () => {
    await Promise.all([refetchActive(), refetchInactive()]);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Remove this listing from the marketplace? It will move to Inactive and buyers will no longer see it.')) {
      return;
    }
    setBusyListingId(id);
    try {
      const result = await updateCarForSaleActive({
        variables: { id, active: false },
      });
      if (!result.data?.updateCarsForSaleById?.carsForSale) {
        throw new Error('Listing was not updated');
      }
      await refreshLists();
      setActiveTab('inactive');
    } catch (error) {
      console.error('Error removing listing:', error);
      alert('Failed to remove listing. Please try again.');
    } finally {
      setBusyListingId(null);
    }
  };

  const handleRelist = async (id: number, wasSold: boolean) => {
    const message = wasSold
      ? 'Relist this vehicle for sale? It will move back to Active and the Sold status will be cleared.'
      : 'Relist this vehicle? It will appear under Active again.';
    if (!window.confirm(message)) return;

    setBusyListingId(id);
    try {
      const result = await relistListing({ variables: { id } });
      const updated = result.data?.updateCarsForSaleById?.carsForSale;
      if (!updated || updated.active !== true || updated.sold !== false) {
        throw new Error('Listing was not relisted');
      }
      await refreshLists();
      setActiveTab('active');
    } catch (error) {
      console.error('Error relisting:', error);
      alert('Failed to relist. Please try again.');
    } finally {
      setBusyListingId(null);
    }
  };

  const handleMarkAsSold = async (id: number) => {
    if (
      !window.confirm(
        'Mark this listing as sold? It will move to Inactive and disappear from search so buyers can no longer contact you about it.'
      )
    ) {
      return;
    }

    setBusyListingId(id);
    try {
      const now = new Date().toISOString();
      const result = await markListingAsSold({
        variables: {
          id,
          sold: true,
          soldToUserId: null,
          soldAt: now,
          active: false,
        },
      });
      const updated = result.data?.updateCarsForSaleById?.carsForSale;
      if (!updated || updated.active !== false || updated.sold !== true) {
        throw new Error('Listing was not marked as sold');
      }
      await refreshLists();
      setActiveTab('inactive');
    } catch (error) {
      console.error('Error marking listing as sold:', error);
      alert('Failed to mark listing as sold. Please try again.');
    } finally {
      setBusyListingId(null);
    }
  };

  const renderListingCard = (car: any, isInactive: boolean = false) => {
    if (!car) return null;
    const isSold = Boolean(car.sold);
    const isBusy = busyListingId === car.id;

    return (
      <div
        key={car?.id}
        className={`listing-card-container ${isInactive ? 'inactive-listing-card' : ''} ${isSold ? 'sold-listing-card' : ''}`}
      >
        <Link to={`/product/${car?.id}`} style={{ textDecoration: 'none', width: '100%' }}>
          <div className="my-listing-card">
            <div className="my-listing-card-image-wrap">
              <img
                src={getFirstImageUrl(car?.imageLoc)}
                className="my-listing-card-image"
                alt="car for sale"
              />
              {isSold && <span className="my-listing-sold-badge">Sold</span>}
              {isInactive && !isSold && (
                <span className="my-listing-inactive-badge">Inactive</span>
              )}
            </div>
            <div className="my-listing-card-content">
              <div className="my-listing-card-price">
                {currencyFormat(parseFloat(car?.price?.toString() ?? "0"))}
              </div>
              <div className="my-listing-card-title">
                {resolveListingTitle(car)}
              </div>
              <div className="my-listing-card-meta">
                <span className="my-listing-card-mileage">
                  {formatMileage(car?.mileage?.toString())}
                </span>
                {isSold && car.soldAt && (
                  <span className="my-listing-sold-date">
                    Sold {new Date(car.soldAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>
        </Link>
        <div className="my-listing-actions">
          {isInactive ? (
            <button
              className="my-listing-action-btn my-listing-action-btn-relist"
              onClick={() => handleRelist(car.id, isSold)}
              disabled={isBusy}
            >
              {isBusy ? 'Working…' : 'Relist'}
            </button>
          ) : (
            <>
              <button
                className="my-listing-action-btn my-listing-action-btn-edit"
                onClick={() => navigate(`/edit-listing/${car.id}`)}
                disabled={isBusy}
              >
                Edit
              </button>
              <button
                className="my-listing-action-btn my-listing-action-btn-sold"
                onClick={() => handleMarkAsSold(car.id)}
                disabled={isBusy || isSold}
              >
                {isBusy ? 'Working…' : 'Mark as Sold'}
              </button>
              <button
                className="my-listing-action-btn my-listing-action-btn-delete"
                onClick={() => handleDelete(car.id)}
                disabled={isBusy}
              >
                Remove
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const activeListings = activeData?.allCarsForSales?.nodes || [];
  const inactiveListings = inactiveData?.allCarsForSales?.nodes || [];
  const hasActiveListings = activeListings.length > 0;
  const hasInactiveListings = inactiveListings.length > 0;

  return (
    <div className={`my-listings-container ${darkMode ? 'dark' : ''}`}>
      <Header />
      <div className="my-listings-header-row">
        <span className="my-listings-title">Your Listings</span>
        <Link to={`/newlisting`}>
          <button className="create-new-listings-btn">
            Create New listing +
          </button>
        </Link>
      </div>

      <div className="my-listings-tabs">
        <button
          className={`my-listings-tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          Active ({activeListings.length})
        </button>
        <button
          className={`my-listings-tab ${activeTab === 'inactive' ? 'active' : ''}`}
          onClick={() => setActiveTab('inactive')}
        >
          Inactive ({inactiveListings.length})
        </button>
      </div>

      {activeTab === 'active' && (
        <div className="my-listings-grid">
          {hasActiveListings ? (
            activeListings.map((car: any) => renderListingCard(car, false))
          ) : (
            <div className="my-listings-empty-state">
              You have no active listings. Create your first listing to get started!
            </div>
          )}
        </div>
      )}

      {activeTab === 'inactive' && (
        <div className="my-listings-grid">
          {hasInactiveListings ? (
            inactiveListings.map((car: any) => renderListingCard(car, true))
          ) : (
            <div className="my-listings-empty-state">
              No inactive listings. Mark a listing as sold or remove it to see it here.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ListingView;
